import type { MediaItem, Rendition } from './types.js';

export interface Connection {
  saveData?: boolean;
  effectiveType?: string;
}

const SLOW = new Set(['slow-2g', '2g', '3g']);

export function isSlow(connection?: Connection): boolean {
  if (!connection) return false;
  return Boolean(connection.saveData) || SLOW.has(connection.effectiveType ?? '');
}

/**
 * Smallest rendition at or above the rendered size in device pixels, capped at
 * the largest available. On a slow connection the grid drops to 400px (US-09).
 */
export function choosePhoto(
  renditions: Rendition[],
  cssWidth: number,
  dpr = 1,
  connection?: Connection,
): Rendition | undefined {
  const images = renditions
    .filter((r) => r.type === 'image/avif' || r.type === 'image/jpeg')
    .sort((a, b) => a.width - b.width);
  if (!images.length) return undefined;
  if (isSlow(connection)) return images[0];
  const needed = Math.ceil(cssWidth * dpr);
  return images.find((r) => r.width >= needed) ?? images[images.length - 1];
}

/**
 * 1080p when the video's rendered width in device pixels reaches 1000 and the
 * connection allows it, otherwise 540p (PRD 7.4). A phone at 390 CSS px and
 * DPR 3 is 1170, so it gets 1080p.
 */
export function chooseClip(
  renditions: Rendition[],
  cssWidth: number,
  dpr = 1,
  connection?: Connection,
): Rendition | undefined {
  const videos = renditions.filter((r) => r.type === 'video/mp4').sort((a, b) => a.width - b.width);
  if (!videos.length) return undefined;
  const low = videos[0]!;
  const high = videos[videos.length - 1]!;
  if (isSlow(connection)) return low;
  return Math.ceil(cssWidth * dpr) >= 1000 ? high : low;
}

/**
 * The still a clip shows before it plays. JPEG first: a <video> poster has to
 * decode in every browser that reaches the album, and AVIF does not.
 */
export function choosePoster(renditions: Rendition[]): Rendition | undefined {
  const posters = renditions.filter((r) => r.key.includes('poster-'));
  const jpeg = posters.filter((r) => r.type === 'image/jpeg').sort((a, b) => b.width - a.width);
  if (jpeg.length) return jpeg[0];
  return posters.sort((a, b) => b.width - a.width)[0];
}

export function srcSet(item: MediaItem, base = ''): string {
  return item.renditions
    .filter((r) => r.type === 'image/avif')
    .sort((a, b) => a.width - b.width)
    .map((r) => `${base}${r.key} ${r.width}w`)
    .join(', ');
}
